import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { submitLead } from "@/lib/api";
import { leadMessage, redirectToWhatsApp } from "@/lib/whatsapp";
import { CheckCircle2, MessageCircle } from "lucide-react";

export default function DemoFormDialog({ trigger, source = "demo", defaultMessage }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [waUrl, setWaUrl] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", website: "", employees: "1-10", message: defaultMessage || "",
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const chatText = leadMessage("Hi GrabNChat, I'd like to book a free demo.", [
      ["Name", form.name],
      ["Phone", form.phone],
      ["Email", form.email],
      ["Company", form.company],
      ["Website", form.website],
      ["Team size", form.employees],
      ["Requirement", form.message],
    ]);
    try {
      await submitLead({ ...form, source });
      setSuccess(true);
      setWaUrl(redirectToWhatsApp(chatText));
      toast.success("Demo request received! Opening WhatsApp…");
    } catch (err) {
      if (err?.kind === "validation") {
        toast.error(err.message);
      } else {
        // Our side failed — don't lose the lead: continue on WhatsApp.
        setSuccess(true);
        setWaUrl(redirectToWhatsApp(chatText));
        toast.message("Opening WhatsApp to complete your request…");
      }
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setSuccess(false);
    setWaUrl(null);
    setForm({ name: "", email: "", phone: "", company: "", website: "", employees: "1-10", message: defaultMessage || "" });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="demo-dialog">
        {success ? (
          <div className="py-6 text-center" data-testid="demo-success">
            <div className="mx-auto w-14 h-14 rounded-full bg-[#25D366]/10 grid place-items-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-[#25D366]" />
            </div>
            <DialogTitle className="text-2xl">You&apos;re in!</DialogTitle>
            <DialogDescription className="mt-2">
              Opening WhatsApp so you can chat with our team on +91 9311 57 2747 — your details are already filled in.
            </DialogDescription>
            {waUrl && (
              <Button asChild className="mt-6 bg-[#25D366] hover:bg-[#1DA851] text-white rounded-full" data-testid="demo-whatsapp-btn">
                <a href={waUrl}>
                  <MessageCircle className="w-4 h-4 mr-2" /> Continue on WhatsApp
                </a>
              </Button>
            )}
            <div>
              <Button variant="ghost" className="mt-2 rounded-full" onClick={() => setOpen(false)} data-testid="demo-close-btn">
                Close
              </Button>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl">Book a free demo</DialogTitle>
              <DialogDescription>See GrabNChat in action—tailored to your business in 30 minutes.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input id="name" required value={form.name} onChange={update("name")} data-testid="demo-input-name" />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input id="phone" required value={form.phone} onChange={update("phone")} data-testid="demo-input-phone" />
                </div>
              </div>
              <div>
                <Label htmlFor="email">Work Email *</Label>
                <Input id="email" type="email" required value={form.email} onChange={update("email")} data-testid="demo-input-email" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="company">Company</Label>
                  <Input id="company" value={form.company} onChange={update("company")} data-testid="demo-input-company" />
                </div>
                <div>
                  <Label htmlFor="website">Website</Label>
                  <Input id="website" value={form.website} onChange={update("website")} data-testid="demo-input-website" />
                </div>
              </div>
              <div>
                <Label>Team Size</Label>
                <Select value={form.employees} onValueChange={(v) => setForm((f) => ({ ...f, employees: v }))}>
                  <SelectTrigger data-testid="demo-input-employees"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-10">1–10</SelectItem>
                    <SelectItem value="11-50">11–50</SelectItem>
                    <SelectItem value="51-200">51–200</SelectItem>
                    <SelectItem value="201-1000">201–1,000</SelectItem>
                    <SelectItem value="1000+">1,000+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="message">Tell us what you need</Label>
                <Textarea id="message" rows={3} value={form.message} onChange={update("message")} data-testid="demo-input-message" />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#25D366] hover:bg-[#1DA851] text-white rounded-full h-11 font-semibold" data-testid="demo-submit-btn">
                {loading ? "Submitting..." : "Book My Free Demo"}
              </Button>
              <p className="text-xs text-slate-500 text-center">No credit card required. We respect your privacy.</p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
